/**
 * Hemodialysis Water Treatment Compliance Resource Page — B2B SEO magnet.
 *
 * Targets high-intent nephrology / dialysis facility queries:
 *   • "hemodialysis water treatment system"
 *   • "AAMI RD52 dialysis water"
 *   • "AAMI 13959 dialysate quality"
 *   • "CMS ESRD Conditions for Coverage water"
 *   • "dialysis water endotoxin"
 */
import SEO, { technicalArticleSchema } from "@/components/SEO";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { CheckCircle2, Droplets, FileText, ShieldCheck, ArrowRight, AlertTriangle, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";

const WATER_LIMITS = [
  { param: "Total viable microbial count", value: "≤ 100 CFU/mL", action: "Action level: 50 CFU/mL" },
  { param: "Endotoxin", value: "≤ 0.25 EU/mL", action: "Action level: 0.125 EU/mL" },
  { param: "Chlorine (free)", value: "< 0.5 mg/L", action: "Must be tested before every treatment shift" },
  { param: "Chloramine", value: "< 0.1 mg/L", action: "Must be tested before every treatment shift" },
  { param: "Aluminum", value: "≤ 0.01 mg/L", action: "Quarterly testing minimum" },
  { param: "Conductivity (RO permeate)", value: "≤ 10 µS/cm typical", action: "Continuously monitored" },
];

const FAQS = [
  {
    q: "What water standards apply to hemodialysis?",
    a: "Hemodialysis water is governed by ANSI/AAMI/ISO 13959 (Water for hemodialysis and related therapies) and ANSI/AAMI/ISO 11663 (Quality of dialysis fluid). AAMI RD52 is the historical U.S. document, but current CMS ESRD Conditions for Coverage (V-tags) reference the ISO-aligned AAMI standards. Ultra-pure dialysate expectations are set in ANSI/AAMI/ISO 23500.",
  },
  {
    q: "What are the CMS ESRD Conditions for Coverage (V-tags) for water?",
    a: "42 CFR §494.40 requires dialysis facilities to comply with the current AAMI water standard. Surveyors cite V-tags V178–V184 (water and dialysate quality), V185–V187 (equipment), and V188–V190 (documentation) — all downstream of AAMI 13959/11663.",
  },
  {
    q: "What water-treatment system does a dialysis clinic need?",
    a: "A typical dialysis water train includes: pre-filtration → carbon tanks (chlorine/chloramine removal) → softeners → reverse osmosis (usually two-pass or RO + DI polishing) → distribution loop with ultrafilters. Every stage requires monitoring, documented testing, and preventative maintenance.",
  },
  {
    q: "How often does dialysis water need to be tested?",
    a: "Free chlorine/chloramine: before every treatment shift. Total viable microbial + endotoxin: monthly minimum (weekly in many facilities). Trace metals (aluminum, copper, fluoride, etc.): quarterly. Comprehensive water quality per AAMI 13959 Table A.1: annually. Testing records must be retained per facility policy and surveyed on demand.",
  },
  {
    q: "How does Sterile Mate help dialysis facilities?",
    a: "Sterile Mate designs, installs, and maintains AAMI-compliant dialysis water treatment systems — including pre-treatment, RO, DI polishing, distribution loops, and ultrafilters. We provide monthly microbial + endotoxin testing, quarterly trace-metal panels, and audit-ready documentation binders across Texas, Louisiana, Alabama, and Mississippi.",
  },
];

export default function HemodialysisWaterPage() {
  const canonical = "/resources/hemodialysis-water-treatment";
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      technicalArticleSchema({
        headline: "Hemodialysis Water Treatment — AAMI 13959 & CMS ESRD Compliance Guide (2024)",
        description:
          "Complete hemodialysis water treatment compliance guide. AAMI 13959, AAMI 11663, CMS ESRD Conditions for Coverage V-tags, endotoxin & microbial limits, testing frequency, and Gulf-region service coverage.",
        url: canonical,
        datePublished: "2026-01-28",
        dateModified: "2026-02-24",
        keywords: [
          "hemodialysis water treatment",
          "hemodialysis water treatment system",
          "AAMI 13959",
          "AAMI 11663",
          "AAMI RD52 dialysis water",
          "CMS ESRD Conditions for Coverage water",
          "dialysis water endotoxin",
          "dialysate quality standards",
          "dialysis reverse osmosis",
        ],
      }),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="hemodialysis-water-page">
      <SEO
        canonical={canonical}
        title="Hemodialysis Water Treatment — AAMI 13959 & CMS ESRD Compliance Guide"
        description="Complete hemodialysis water treatment compliance guide. AAMI 13959/11663, CMS ESRD V-tags, endotoxin + microbial limits, and Gulf-region dialysis water system services."
        keywords={[
          "hemodialysis water treatment",
          "AAMI 13959",
          "AAMI RD52",
          "dialysis reverse osmosis",
          "CMS ESRD water compliance",
        ]}
        schema={schema}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#0a1a35] via-[#0b2447] to-[#0056A8] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(34,211,238,0.18),transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-cyan-300 mb-4">
            Compliance Resource · Hemodialysis
          </p>
          <h1 className="text-4xl md:text-6xl font-black leading-[1.05] max-w-4xl">
            Hemodialysis Water Treatment — AAMI &amp; CMS Compliance.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed">
            Every hemodialysis session pushes ~120 liters of water across the dialyzer membrane
          into direct contact with the patient&apos;s blood. AAMI 13959/11663 + CMS ESRD V-tags
            define the water-quality bar — and surveyors check documentation every visit. Serving
            dialysis facilities across <strong>Texas, Louisiana, Alabama, and Mississippi.</strong>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
              <a href="mailto:info@sterilemate.com?subject=Dialysis water system quote">
                <Droplets className="w-5 h-5 mr-2" />
                Request Dialysis Water System Quote
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              <Link to="/resources/joint-commission-water-management">
                <FileText className="w-5 h-5 mr-2" />
                See WMP Compliance Guide
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* LIMITS TABLE */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-2">Key AAMI 13959 Water-Quality Limits</h2>
        <p className="text-slate-600 mb-8 max-w-3xl">
          The following limits apply to water used to prepare dialysate. Action levels are set at
          50% of the maximum to trigger investigation before the limit is exceeded.
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-800">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Parameter</th>
                <th className="text-left px-4 py-3 font-bold">Maximum Limit</th>
                <th className="text-left px-4 py-3 font-bold">Monitoring / Action</th>
              </tr>
            </thead>
            <tbody>
              {WATER_LIMITS.map((r) => (
                <tr key={r.param} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-semibold text-slate-900">{r.param}</td>
                  <td className="px-4 py-3 font-mono text-slate-800">{r.value}</td>
                  <td className="px-4 py-3 text-slate-600">{r.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Source: ANSI/AAMI/ISO 13959:2014 & ANSI/AAMI/ISO 11663:2014. Consult the current standards for complete specifications.
        </p>
      </section>

      {/* HOW WE HELP */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-black text-slate-900 mb-2">Complete Dialysis Water Services</h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            From new-build system design to daily operation and CMS-survey documentation — one
            dispatch center serving Gulf-region nephrology.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                icon: Droplets,
                title: "System Design & Install",
                text: "Pre-treatment carbon, softeners, two-pass RO, DI polishing, distribution loop, and ultrafilters — sized for your patient chair count and future expansion.",
              },
              {
                icon: Activity,
                title: "Testing & Monitoring",
                text: "Monthly microbial + endotoxin cultures, quarterly trace-metal panels, annual AAMI 13959 Table A.1 comprehensive, plus daily chlorine/chloramine test strips.",
              },
              {
                icon: ShieldCheck,
                title: "PM + CMS Documentation",
                text: "Scheduled RO membrane cleaning, carbon change-out, distribution-loop disinfection, and audit-ready binders that map to every V-tag surveyors will request.",
              },
            ].map((f) => (
              <div key={f.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <f.icon className="w-6 h-6 text-blue-700 mb-3" />
                <p className="font-bold text-slate-900 mb-1">{f.title}</p>
                <p className="text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-8">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details
              key={i}
              className="group rounded-xl border border-slate-200 bg-white p-5 open:shadow-md transition-shadow"
            >
              <summary className="cursor-pointer flex items-center justify-between font-bold text-slate-900">
                {f.q}
                <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-slate-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Warning banner */}
      <section className="bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-6 py-8 flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-900 mb-1">
              CMS ESRD water findings are always Condition-level.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              Water and dialysate quality V-tags are among the most-cited ESRD findings — and
              they are almost always classified at the Condition level, threatening
              certification.{" "}
              <a href="mailto:info@sterilemate.com?subject=Dialysis water system audit" className="font-semibold text-amber-800 underline">
                Request a dialysis water audit
              </a>{" "}
              — Gulf-region dispatch, typically responds within one business day.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
